import { useEffect, useRef, useCallback, useState } from 'react';

export interface Position {
  sessionId: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number;
  heading?: number;
}

interface UseWebSocketOptions {
  wsEndpoint: string;
  sessionIds?: string[];
  onPosition?: (position: Position) => void;
  onError?: (error: Error) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export const useWebSocket = ({
  wsEndpoint,
  sessionIds = [],
  onPosition,
  onError,
  onConnectionChange,
}: UseWebSocketOptions) => {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    try {
      const ws = new WebSocket(wsEndpoint);

      ws.onopen = () => {
        console.log('[WebSocket] Connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        onConnectionChange?.(true);

        // Subscribe to all sessionIds (use current sessionIds from closure)
        if (sessionIds && sessionIds.length > 0) {
          sessionIds.forEach((sessionId) => {
            ws.send(
              JSON.stringify({
                action: 'subscribe',
                sessionId,
              })
            );
            console.log(`[WebSocket] Subscribed to session: ${sessionId}`);
          });
        }
      };

      ws.onmessage = (event) => {
        try {
          const position = JSON.parse(event.data) as Position;
          onPosition?.(position);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      ws.onerror = (event) => {
        console.error('[WebSocket] Error:', event);
        const error = new Error('WebSocket error occurred');
        onError?.(error);
      };

      ws.onclose = () => {
        console.log('[WebSocket] Disconnected');
        setIsConnected(false);
        onConnectionChange?.(false);

        // Attempt to reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `[WebSocket] Reconnecting... (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
          );
          reconnectTimeoutRef.current = setTimeout(connect, reconnectDelay);
        } else {
          console.error('[WebSocket] Max reconnection attempts reached');
          onError?.(new Error('Failed to reconnect WebSocket after max attempts'));
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }, [wsEndpoint, onPosition, onError, onConnectionChange]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    onConnectionChange?.(false);
  }, [onConnectionChange]);

  const subscribe = useCallback((sessionId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: 'subscribe',
          sessionId,
        })
      );
      console.log(`[WebSocket] Subscribed to session: ${sessionId}`);
    } else {
      console.warn('[WebSocket] Cannot subscribe: WebSocket not connected');
    }
  }, []);

  const unsubscribe = useCallback((sessionId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: 'unsubscribe',
          sessionId,
        })
      );
      console.log(`[WebSocket] Unsubscribed from session: ${sessionId}`);
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Re-subscribe when sessionIds change
  useEffect(() => {
    if (isConnected && wsRef.current?.readyState === WebSocket.OPEN) {
      sessionIds.forEach((sessionId) => {
        wsRef.current?.send(
          JSON.stringify({
            action: 'subscribe',
            sessionId,
          })
        );
      });
    }
  }, [sessionIds, isConnected]);

  return {
    isConnected,
    subscribe,
    unsubscribe,
    disconnect,
  };
};
